import { type MouseEvent, useEffect, useState } from "react";
import {
  COPY_CODING_AGENT_COPIED,
  COPY_CODING_AGENT_HELP,
  COPY_CODING_AGENT_LABEL,
  type CodingAgentIssue,
  codingAgentPrompt,
} from "@/utils/codingAgentPrompt";

type Props = {
  issue: CodingAgentIssue;
  variant: "row" | "detail";
};

export default function CopyCodingAgentPrompt({ issue, variant }: Props) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }

    const timer = window.setTimeout(() => setCopied(false), 4000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  async function copyPrompt() {
    try {
      await navigator.clipboard.writeText(codingAgentPrompt(issue));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    void copyPrompt();
  }

  if (variant === "row") {
    return (
      <button
        type="button"
        className={`cursor-pointer text-left text-sm font-medium hover:underline ${
          copied ? "text-primary" : "text-gray-600"
        }`}
        onClick={handleClick}
      >
        {copied ? COPY_CODING_AGENT_COPIED : COPY_CODING_AGENT_LABEL}
      </button>
    );
  }

  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-3">
      <p className="mb-2.5 text-sm leading-relaxed text-gray-500" aria-live="polite">
        {copied ? COPY_CODING_AGENT_COPIED : COPY_CODING_AGENT_HELP}
      </p>
      <button type="button" className={`btn ${copied ? "bg-primary-50 text-primary" : ""}`} onClick={handleClick}>
        {copied ? "Copied" : COPY_CODING_AGENT_LABEL}
      </button>
    </div>
  );
}
